"use client";

import { useState } from "react";
import { Funnel as Filter, Settings } from 'lucide-react';
import { EcommerceProductsProvider, ProductSortDropdown, ProductsFilter, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { WP_Query } from "@/integrations/wordpress/wp_query";
declare const wvcClient: any;
function ProductsGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    total,
    hasNext,
    hasPrev,
    currentPage,
    totalPages,
    nextPage,
    prevPage,
    setPage
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="aspect-[4/5] rounded-md bg-[color:var(--muted)]/40 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="rounded-md border border-[color:var(--border)] bg-[color:var(--card)] p-8 text-center">
        <p className="text-sm text-[color:var(--muted-foreground)] font-default">
          Failed to load industrial components.{" "}
          <Button variant="link" size="sm" onClick={refetch} className="h-auto p-0 underline text-[color:var(--textPrimary)]">
            Retry
          </Button>
        </p>
      </div>;
  }
  if (products.length === 0) {
    return <div className="rounded-md border border-[color:var(--border)] bg-[color:var(--card)] p-12 text-center">
        <p className="font-default text-base text-[color:var(--muted-foreground)]">
          No parts found in this category.
        </p>
      </div>;
  }
  return <>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {products.map((product, i) => <article key={product.id} data-index={i} className="group relative flex flex-col rounded-md border border-[color:var(--border)] bg-[color:var(--card)] p-3 transition-transform duration-0 ease-out hover:border-[color:var(--textPrimary)]">
            <span aria-hidden className="pointer-events-none absolute top-0 left-0 h-2 w-2 border-t border-l border-[color:var(--textPrimary)] opacity-0 group-hover:opacity-100 transition-opacity duration-0" />
            <span aria-hidden className="pointer-events-none absolute top-0 right-0 h-2 w-2 border-t border-r border-[color:var(--textPrimary)] opacity-0 group-hover:opacity-100 transition-opacity duration-0" />
            <span aria-hidden className="pointer-events-none absolute bottom-0 left-0 h-2 w-2 border-b border-l border-[color:var(--textPrimary)] opacity-0 group-hover:opacity-100 transition-opacity duration-0" />
            <span aria-hidden className="pointer-events-none absolute bottom-0 right-0 h-2 w-2 border-b border-r border-[color:var(--textPrimary)] opacity-0 group-hover:opacity-100 transition-opacity duration-0" />

            <Link to={product.link} className="block">
              <div className="overflow-hidden rounded-md bg-[color:var(--muted)]">
                <div className="aspect-square w-full">
                  {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.05]" loading="lazy" decoding="async" /> : <div className="flex h-full w-full items-center justify-center border-2 border-dashed border-[color:var(--border)] bg-[color:var(--muted)]/20 text-[color:var(--muted-foreground)]">
                      <Settings className="size-8 opacity-50" />
                    </div>}
                </div>
              </div>
            </Link>

            <div className="mt-4 px-1 pb-1">
              <Link to={product.link} className="mt-2 block">
                <h3 className="font-default text-lg font-bold leading-tight tracking-tight text-[color:var(--primary)]">
                  {product.title}
                </h3>
              </Link>

              <p className="mt-1 font-mono text-sm font-bold text-[color:var(--accent)]">
                {product.price > 0 ? `R ${product.price.toLocaleString()}` : "PRICE ON REQUEST"}
              </p>

              <div className="mt-3 flex flex-col items-start justify-between gap-3">
                <div className="shrink-0 w-full">
                  <Link to={product.link} className="inline-block w-full text-center bg-[color:var(--accent)] text-white rounded-md font-mono tracking-wider text-[10px] uppercase px-4 py-2 hover:brightness-110 transition-all duration-0">
                    VIEW DETAILS
                  </Link>
                </div>
              </div>
            </div>
          </article>)}
      </div>

      {totalPages > 1 && <nav aria-label="Pagination" className="mt-12 flex items-center justify-center gap-4">
          <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrev} className="rounded-md border-[color:var(--border)] bg-transparent font-mono text-[10px] uppercase tracking-widest text-[color:var(--primary)] hover:bg-[color:var(--accent)] hover:text-white transition-all duration-0">
            Prev
          </Button>

          <div className="flex items-center gap-2">
            {Array.from({
          length: totalPages
        }).map((_, idx) => {
          const page = idx + 1;
          const isActive = page === currentPage;
          return <Button key={page} variant="ghost" size="sm" onClick={() => setPage(page)} aria-current={isActive ? "page" : undefined} data-index={idx} className={"font-mono text-xs tracking-widest h-auto px-2 " + (isActive ? "text-[color:var(--textPrimary)] font-bold border-b-2 border-[color:var(--textPrimary)]" : "text-[color:var(--muted-foreground)] hover:text-[color:var(--primary)]")}>
                  {String(page).padStart(2, "0")}
                </Button>;
        })}
          </div>

          <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNext} className="rounded-md border-[color:var(--border)] bg-transparent font-mono text-[10px] uppercase tracking-widest text-[color:var(--primary)] hover:bg-[color:var(--accent)] hover:text-white transition-all duration-0">
            Next
          </Button>
        </nav>}

      <p className="mt-6 text-center font-mono text-[10px] uppercase tracking-widest text-[color:var(--muted-foreground)]">
        [ {String(total).padStart(3, "0")} TOTAL COMPONENTS INDEXED ]
      </p>
    </>;
}
function ProductCountDisplay() {
  const {
    total
  } = useEcommerceProducts();
  return <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--muted-foreground)]">
      <span className="text-[color:var(--textPrimary)]">
        § {String(total ?? 0).padStart(2, "0")}
      </span>
      <span className="ml-2">PARTS_AVAILABLE</span>
    </div>;
}
export default function ShopContent() {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const current_wp_query_params = typeof window !== "undefined" && (window as any).wvcClient ? (window as any).wvcClient.getCurrentWPQueryParams() : {};
  const wp_query = new WP_Query(current_wp_query_params);
  return <section data-section-id="2118" id="shop-content" className="relative bg-[color:var(--background)] text-[color:var(--primary)] py-20 md:py-28">
      <div className="mx-auto w-full max-w-[1440px] px-5 md:px-8 lg:px-14">
        
        <EcommerceProductsProvider wp_query={wp_query}>
          <div className="grid grid-cols-12 gap-6 lg:gap-8">
            <div className="col-span-12">
              <div className="mb-6 flex items-center justify-between">
                <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                  <SheetTrigger asChild>
                    <Button variant="outline" size="sm" className="rounded-md border-[color:var(--border)] bg-transparent font-mono text-[10px] uppercase tracking-widest text-[color:var(--primary)]">
                      <Filter className="size-4 mr-2" />
                      Filter Parts
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-[88%] overflow-y-auto bg-[color:var(--card)] p-6 sm:max-w-sm border-r border-[color:var(--textPrimary)]">
                    <SheetHeader className="p-0">
                      <SheetTitle className="font-default text-xl font-bold tracking-tight text-[color:var(--primary)]">
                        Technical Filters
                      </SheetTitle>
                    </SheetHeader>
                    <div className="mt-2 mb-6 flex items-center gap-2">
                      <span className="h-px flex-1 bg-[color:var(--border)]" />
                      <span aria-hidden className="h-1 w-1 bg-[color:var(--textPrimary)]" />
                    </div>
                    <ProductsFilter />
                    <SheetClose asChild>
                      <Button className="mt-6 w-full bg-[color:var(--signal-green)] text-white px-8 py-4 hover:brightness-110 transition-all duration-0">
                        Apply Filters
                      </Button>
                    </SheetClose>
                  </SheetContent>
                </Sheet>
              </div>

              <div className="mb-6 flex items-center justify-between gap-4 border-b border-[color:var(--border)] pb-4">
                <ProductCountDisplay />
                <div className="flex items-center gap-3">
                  <span className="hidden font-mono text-[10px] uppercase tracking-widest text-[color:var(--muted-foreground)] sm:inline">
                    Sort_By
                  </span>
                  <ProductSortDropdown label="" />
                </div>
              </div>

              <ProductsGridConsumer />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>;
}